import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  FlatList,
} from 'react-native';
import { useParams, useNavigate } from 'react-router-dom';
import { Film, Play, Heart, ArrowLeft, Pause, Volume2, VolumeX, Maximize, Minimize, Tv, Copy, Check } from 'lucide-react';
import { useEpisodeDetail, useEpisodes, useSeriesDetail } from '../../hooks/useSeries';
import { useAddToWatchlist, useRemoveFromWatchlist } from '../../hooks/useWatchlist';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModals } from '../../contexts/AuthModalsContext';
import { useSaveWatchProgress } from '../../hooks/useWatchProgress';
import { useVideoPixelTracking } from '../../hooks/usePixelTracking';
import { useResponsive } from '../../hooks/useResponsive';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../../theme/netflixTheme';
import { AuthRequiredOverlay } from '../../components/AuthRequiredOverlay';
import { ReportModal } from '../../components/ReportModal';
import { AlertTriangle } from 'lucide-react';
import { dramatstraAPI } from '../../services/api';

declare global {
  interface Window {
    Hls: any;
    cast: any;
    chrome: any;
    __goog_cast_api_available: boolean;
  }
}

class BunnyPlayer {
  private videoElement: HTMLVideoElement;
  private hls: any;
  private listeners: { [key: string]: Function[] } = {};
  private playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];
  private currentPlaybackRate = 1;
  private isFullscreen = false;
  private _isCasting = false;

  constructor(videoElement: HTMLVideoElement, m3u8Url: string) {
    this.videoElement = videoElement;
    this.listeners = {
      ready: [],
      play: [],
      pause: [],
      timeupdate: [],
      ended: [],
      error: [],
      fullscreenchange: [],
      castupdate: [],
    };

    this.initialize(m3u8Url);
    this.initializeCast();
  }

  private initialize(m3u8Url: string) {
    const hlsSupported = () => {
      return typeof window !== 'undefined' && window.Hls && window.Hls.isSupported && window.Hls.isSupported();
    };

    if (hlsSupported()) {
      try {
        this.hls = new window.Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: false,
        });

        this.hls.on(window.Hls.Events.ERROR, (_event: string, data: any) => {
          console.warn('HLS Error:', data);
          if (data.fatal) {
            console.error('HLS Fatal Error:', data);
            this.emit('error');
          }
        });

        this.hls.loadSource(m3u8Url);
        this.hls.attachMedia(this.videoElement);

        this.hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          this.emit('ready');
          this.videoElement.play().catch(err => console.warn('Autoplay blocked:', err));
        });
      } catch (error) {
        console.error('HLS.js initialization error:', error);
        this.fallbackLoad(m3u8Url);
      }
    } else {
      console.warn('HLS.js not available, using fallback');
      this.fallbackLoad(m3u8Url);
    }

    this.videoElement.addEventListener('play', () => this.emit('play'));
    this.videoElement.addEventListener('pause', () => this.emit('pause'));
    this.videoElement.addEventListener('timeupdate', () => this.emit('timeupdate'));
    this.videoElement.addEventListener('ended', () => this.emit('ended'));
    this.videoElement.addEventListener('loadedmetadata', () => {
      console.log('Video metadata loaded:', this.videoElement.duration);
    });
    this.videoElement.addEventListener('error', (e) => {
      console.error('Video playback error:', e, this.videoElement.error);
      this.emit('error');
    });
    this.videoElement.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
      this.emit('fullscreenchange');
    });
  }

  private fallbackLoad(m3u8Url: string) {
    console.log('Loading via fallback (native HLS):', m3u8Url);
    this.videoElement.src = m3u8Url;
    this.videoElement.addEventListener('loadedmetadata', () => {
      this.emit('ready');
    }, { once: true });
  }

  private initializeCast() {
    try {
      if (window.cast) {
        this.setupCast();
      }
    } catch (error) {
      console.warn('Cast initialization skipped:', error);
    }
  }

  private setupCast() {
    try {
      if (!window.cast || !window.chrome) {
        console.warn('Cast SDK not available');
        return;
      }
      const context = window.cast.framework.CastContext.getInstance();
      context.setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });
    } catch (error) {
      console.warn('Cast API setup error:', error);
    }
  }

  on(event: string, callback: Function) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  emit(event: string) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(cb => cb());
    }
  }

  play() {
    this.videoElement.play();
  }

  pause() {
    this.videoElement.pause();
  }

  setVolume(volume: number) {
    this.videoElement.volume = Math.max(0, Math.min(1, volume));
  }

  getVolume(): number {
    return this.videoElement.volume;
  }

  setCurrentTime(time: number) {
    this.videoElement.currentTime = time;
  }

  getCurrentTime(): number {
    return this.videoElement.currentTime;
  }

  getDuration(): number {
    return this.videoElement.duration;
  }

  setPlaybackRate(rate: number) {
    if (this.playbackRates.includes(rate)) {
      this.currentPlaybackRate = rate;
      this.videoElement.playbackRate = rate;
    }
  }

  getPlaybackRate(): number {
    return this.currentPlaybackRate;
  }

  getPlaybackRates(): number[] {
    return this.playbackRates;
  }

  requestFullscreen() {
    if (this.videoElement.requestFullscreen) {
      this.videoElement.requestFullscreen();
    } else if ((this.videoElement as any).webkitRequestFullscreen) {
      (this.videoElement as any).webkitRequestFullscreen();
    } else if ((this.videoElement as any).mozRequestFullScreen) {
      (this.videoElement as any).mozRequestFullScreen();
    }
  }

  exitFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.requestFullscreen();
    }
  }

  async requestPictureInPicture() {
    try {
      await this.videoElement.requestPictureInPicture();
    } catch (error) {
      console.error('PiP not supported:', error);
    }
  }

  async requestCast() {
    try {
      const context = window.cast.framework.CastContext.getInstance();
      context.requestSession();
      this._isCasting = true;
      this.emit('castupdate');
    } catch (error) {
      console.error('Cast not available:', error);
    }
  }

  stopCasting() {
    try {
      const context = window.cast.framework.CastContext.getInstance();
      context.endCurrentSession(true);
      this._isCasting = false;
      this.emit('castupdate');
    } catch (error) {
      console.error('Error stopping cast:', error);
    }
  }

  getIsCasting(): boolean {
    return this._isCasting;
  }

  setQuality(qualityLevel: number) {
    if (this.hls) {
      if (qualityLevel === -1) {
        this.hls.currentLevel = -1;
      } else {
        this.hls.currentLevel = qualityLevel;
      }
    }
  }

  getAvailableQualities() {
    if (this.hls) {
      return this.hls.levels.map((level: any, index: number) => ({
        index,
        height: level.height,
        bitrate: level.bitrate,
        name: `${level.height}p`,
      }));
    }
    return [];
  }

  setTheaterMode(enabled: boolean) {
    if (enabled) {
      this.videoElement.parentElement?.classList.add('theater-mode');
    } else {
      this.videoElement.parentElement?.classList.remove('theater-mode');
    }
  }

  mute() {
    this.videoElement.muted = true;
  }

  unmute() {
    this.videoElement.muted = false;
  }

  isMuted(): boolean {
    return this.videoElement.muted;
  }

  destroy() {
    if (this.hls) {
      this.hls.destroy();
    }
    this.listeners = {};
  }
}

export default function PlayerScreen(): JSX.Element {
  const { seriesId, episodeNumber: episodeNumberStr } = useParams<{ seriesId: string; episodeNumber: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { openAuthFlowLogin, openAuthFlowSignup } = useAuthModals();
  const { trackPlay } = useVideoPixelTracking();
  const { isMobile } = useResponsive();
  const saveWatchProgress = useSaveWatchProgress();
  const episodeNumber = parseInt(episodeNumberStr || '1', 10);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [isFullscreen, setIsFullscreenState] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [qualities, setQualities] = useState<any[]>([]);
  const [currentQuality, setCurrentQuality] = useState('auto');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [episodeSearchQuery, setEpisodeSearchQuery] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<BunnyPlayer | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>(null);

  const { data: series } = useSeriesDetail(isAuthenticated ? seriesId : null);
  const { data: episode, isLoading } = useEpisodeDetail(
    isAuthenticated ? seriesId : null,
    isAuthenticated ? episodeNumber : null
  );
  const { data: episodes } = useEpisodes(isAuthenticated ? seriesId : null);
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();

  // Log detalhado do episódio quando carregar
  useEffect(() => {
    if (episode && !isLoading) {
      const ep = episode as any;
      console.log('📺 [PlayerScreen] Episódio carregado:');
      console.log(`   - ID: ${episode.id}`);
      console.log(`   - Título: ${episode.title}`);
      console.log(`   - Origin: ${ep.origin || 'não definido'}`);
      console.log(`   - m3u8_url_bunny: ${episode.m3u8_url_bunny ? '✅ ' + episode.m3u8_url_bunny.substring(0, 60) + '...' : '❌ nula'}`);
      console.log(`   - m3u8_url_source: ${ep.m3u8_url_source ? '✅ ' + ep.m3u8_url_source.substring(0, 60) + '...' : '❌ nula'}`);
      console.log(`   - bunny_video_id: ${episode.bunny_video_id ? '✅' : '❌'}`);
      console.log(`   - mp4_urls: ${episode.mp4_urls?.length ? '✅ ' + episode.mp4_urls.length : '❌'}`);
    }
  }, [episode, isLoading]);

  // in_watchlist vem da rota GET /streaming/series/{id}
  const inWatchlist = series?.in_watchlist === true;

  // Filtrar episódios por busca
  const filteredEpisodes = episodes?.filter((ep) => {
    const query = episodeSearchQuery.toLowerCase();
    return (
      ep.episode_number.toString().includes(query) ||
      ep.title.toLowerCase().includes(query)
    );
  }) || [];

  const handleToggleWatchlist = () => {
    if (!seriesId) return;

    if (inWatchlist) {
      removeFromWatchlist.mutate(seriesId, {
        onSuccess: () => {
          toast.dismiss();
          toast.success('Removido dos favoritos');
        },
        onError: (error: any) => {
          toast.dismiss();
          if (error?.response?.status === 404) {
            toast.success('Removido dos favoritos');
          } else {
            toast.error('Erro ao remover dos favoritos');
          }
        },
      });
    } else {
      addToWatchlist.mutate(seriesId, {
        onSuccess: () => {
          toast.dismiss();
          toast.success('Adicionado aos favoritos!');
        },
        onError: (error: any) => {
          toast.dismiss();
          if (error?.response?.status === 409) {
            toast.success('Já está na sua lista de favoritos');
          } else {
            toast.error('Erro ao adicionar aos favoritos');
          }
        },
      });
    }
  };

  const handleMarkAsWatched = () => {
    if (!episode) return;

    saveWatchProgress.mutate(
      {
        episodeId: episode.id,
        watchedSeconds: episode.duration_seconds || 3600,
        durationSeconds: episode.duration_seconds || 3600,
      },
      {
        onSuccess: () => {
          toast.success('✅ Episódio marcado como assistido!');

          // Verificar se há próximo episódio
          const nextEpisode = episodes?.find(
            (e) => e.episode_number === episodeNumber + 1
          );

          if (nextEpisode) {
            // Auto-play para o próximo episódio
            setTimeout(() => {
              navigate(`/watch/${seriesId}/${nextEpisode.episode_number}`);
            }, 500);
          } else {
            // Se é o último episódio, mostrar mensagem
            toast.success('🎉 Série finalizada! Parabéns!');
          }
        },
        onError: () => {
          toast.error('Erro ao marcar como assistido');
        },
      }
    );
  };

  const responsiveMainWrapperStyle = {
    maxWidth: '1440px',
    margin: '0 auto',
    width: '100%',
    padding: isMobile ? `0 ${NETFLIX_SPACING.md}px` : `0 ${NETFLIX_SPACING.xl}px`,
  };

  const responsiveInfoTitleStyle = isMobile ? { fontSize: 20 } : {};
  const responsiveInfoDescriptionStyle = isMobile ? { fontSize: 12 } : {};
  const responsiveEpisodesTitleStyle = isMobile ? { fontSize: 14 } : {};
  const responsiveActionButtonsStyle = isMobile ? { flexDirection: 'column' as const, gap: NETFLIX_SPACING.sm } : { flexDirection: 'row' as const, gap: NETFLIX_SPACING.md };


  useEffect(() => {
    if (episode && isAuthenticated) {
      const videoName = `${episode.title} - Ep. ${episode.episode_number}`;
      trackPlay(videoName, episode.duration_seconds || 3600);
    }
  }, [episode, isAuthenticated, trackPlay]);

  // Salvar progresso a cada 5 segundos
  useEffect(() => {
    if (!episode || !isPlaying || !isAuthenticated) return;

    const saveInterval = setInterval(() => {
      if (videoRef.current && episode.id) {
        saveWatchProgress.mutate({
          episodeId: episode.id,
          watchedSeconds: Math.round(videoRef.current.currentTime),
          durationSeconds: episode.duration_seconds || 3600,
        });
      }
    }, 10000); // Salva a cada 10 segundos

    return () => clearInterval(saveInterval);
  }, [episode, isPlaying, isAuthenticated, saveWatchProgress]);

  // 🔄 Restaurar progresso anterior quando episódio carregar
  useEffect(() => {
    const restoreProgress = async () => {
      if (!episode?.id || !isAuthenticated) return;

      try {
        const response = await dramatstraAPI.watchHistory.getProgress(episode.id);
        const progress = (response.data as any)?.data;

        if (progress && progress.watched_seconds > 0 && videoRef.current) {
          const resumeTime = Math.max(0, progress.watched_seconds - 5); // 5 seg antes
          console.log(`⏮️ [PlayerScreen] Restaurando progresso: ${resumeTime}s / ${episode.duration_seconds}s`);

          // Aguardar o vídeo estar pronto antes de seekar
          const seekWhenReady = () => {
            if (videoRef.current && !isNaN(videoRef.current.duration)) {
              videoRef.current.currentTime = resumeTime;
              videoRef.current.removeEventListener('loadedmetadata', seekWhenReady);
            }
          };

          if (videoRef.current.duration) {
            videoRef.current.currentTime = resumeTime;
          } else {
            videoRef.current.addEventListener('loadedmetadata', seekWhenReady);
          }
        }
      } catch {
        console.log('📊 [PlayerScreen] Sem progresso anterior para este episódio');
      }
    };

    restoreProgress();
  }, [episode?.id, episode?.duration_seconds, isAuthenticated]);

  useEffect(() => {
    if (videoRef.current && !playerRef.current) {
      try {
        // Se houver m3u8_url_bunny válido, usar HLS player
        if (episode?.m3u8_url_bunny && episode.m3u8_url_bunny.trim() !== '') {
          playerRef.current = new BunnyPlayer(videoRef.current, episode.m3u8_url_bunny);

          playerRef.current.on('ready', () => {
            setIframeLoaded(true);
            setDuration(videoRef.current?.duration || 0);
            const quals = playerRef.current?.getAvailableQualities() || [];
            setQualities(quals);
          });

          playerRef.current.on('play', () => setIsPlaying(true));
          playerRef.current.on('pause', () => setIsPlaying(false));
          playerRef.current.on('timeupdate', () => {
            setCurrentTime(videoRef.current?.currentTime || 0);
          });
          playerRef.current.on('ended', () => setIsPlaying(false));
        } else if (episode?.m3u8_url_source) {
          // Se não houver m3u8_url_bunny, usar m3u8_url_source (arquivo direto como mkv/mp4 ou proxy DroidTech)
          console.log(`🎬 [PlayerScreen] Carregando vídeo:`, episode.m3u8_url_source.substring(0, 80) + '...');
          videoRef.current.src = episode.m3u8_url_source;
          setIframeLoaded(true);
        } else if (episode?.mp4_urls?.length) {
          // Se não houver m3u8 mas houver mp4, usar o primeiro (maior qualidade)
          const highestQuality = episode.mp4_urls[0];
          videoRef.current.src = highestQuality.url;
          setIframeLoaded(true);
        }

        videoRef.current.addEventListener('loadedmetadata', () => {
          setDuration(videoRef.current?.duration || 0);
        });

        videoRef.current.addEventListener('play', () => setIsPlaying(true));
        videoRef.current.addEventListener('pause', () => setIsPlaying(false));

        videoRef.current.addEventListener('timeupdate', () => {
          setCurrentTime(videoRef.current?.currentTime || 0);
        });

        videoRef.current.addEventListener('ended', () => setIsPlaying(false));

        videoRef.current.addEventListener('volumechange', () => {
          setVolumeState(videoRef.current?.volume || 0);
        });

        videoRef.current.addEventListener('ratechange', () => {
          setPlaybackRateState(videoRef.current?.playbackRate || 1);
        });

        videoRef.current.addEventListener('fullscreenchange', () => {
          setIsFullscreenState(!!document.fullscreenElement);
        });
      } catch (error) {
        console.error('Error initializing player:', error);
        setIframeLoaded(true);
      }
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [episode?.m3u8_url_bunny, episode?.m3u8_url_source, episode?.mp4_urls, seriesId, episodeNumber]);

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  };

  // Verificando autenticação - mostrar loading
  if (isAuthLoading) {
    return (
      <View style={styles.container}>
        <div style={playerContainerStyle}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
            <Text style={styles.loadingText}>Carregando...</Text>
          </View>
        </div>
      </View>
    );
  }

  // Loading episódio - mostrar antes de qualquer outra coisa
  if (isLoading) {
    return (
      <View style={styles.container}>
        <div style={playerContainerStyle}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
            <Text style={styles.loadingText}>Carregando episódio...</Text>
          </View>
        </div>
      </View>
    );
  }

  // Se não autenticado
  if (!isAuthenticated) {
    return <AuthRequiredOverlay onOpenLogin={openAuthFlowLogin} onOpenSignup={openAuthFlowSignup} />;
  }

  // Se não temos episódio depois do loading
  if (!episode) {
    return (
      <View style={styles.container}>
        <div style={playerContainerStyle}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
            <Text style={styles.loadingText}>Carregando episódio...</Text>
          </View>
        </div>
      </View>
    );
  }

  // Erro: série em breve (ongoing) ou sem URL de streaming
  const hasStreamingUrl = !!(episode?.m3u8_url_bunny || episode?.m3u8_url_source || episode?.mp4_urls?.length);
  const isSeriesOngoing = series?.status === 'ongoing';
  const shouldBlockEpisode = !isLoading && episode && (isSeriesOngoing || !hasStreamingUrl);

  if (shouldBlockEpisode) {
    console.warn('🚫 [PlayerScreen] Episódio bloqueado:');
    console.warn(`   - Série em breve (ongoing): ${isSeriesOngoing}`);
    console.warn(`   - URL de streaming disponível: ${hasStreamingUrl}`);
    if (!hasStreamingUrl) {
      console.warn(`   - m3u8_url_bunny: ${episode.m3u8_url_bunny ? '✅' : '❌'}`);
      console.warn(`   - m3u8_url_source: ${episode.m3u8_url_source ? '✅' : '❌'}`);
      console.warn(`   - mp4_urls: ${episode.mp4_urls?.length ? '✅' : '❌'}`);
    }
    return (
      <View style={styles.container}>
        <div style={playerContainerPlaceholderStyle}>
          <View style={styles.placeholderContent}>
            <Film size={64} color={NETFLIX_COLORS.primary} />
            <Text style={styles.placeholderTitle}>Em Breve</Text>
            <Text style={styles.placeholderText}>
              Este episódio estará disponível em breve
            </Text>
          </View>
        </div>

        <div style={responsiveMainWrapperStyle}>
          <View style={styles.info}>
            <Text style={[styles.infoTitle, responsiveInfoTitleStyle]}>{episode.title}</Text>
            {episode.description && (
              <Text style={[styles.infoDescription, responsiveInfoDescriptionStyle]}>{episode.description}</Text>
            )}
          </View>

          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.button, styles.backButton]}
              onPress={() => navigate(-1)}
            >
              <ArrowLeft size={20} color={NETFLIX_COLORS.textPrimary} />
              <Text style={styles.buttonText}>Voltar</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                inWatchlist ? styles.favoriteButtonRemove : styles.favoriteButton,
              ]}
              onPress={handleToggleWatchlist}
              disabled={addToWatchlist.isPending || removeFromWatchlist.isPending}
            >
              <Heart
                size={20}
                color={inWatchlist ? NETFLIX_COLORS.white : NETFLIX_COLORS.textPrimary}
                fill={inWatchlist ? NETFLIX_COLORS.danger : 'none'}
              />
              <Text style={[styles.buttonText, inWatchlist && styles.favoriteButtonRemoveText]}>
                {inWatchlist ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
              </Text>
            </TouchableOpacity>
          </View>

          {episodes && episodes.length > 0 && (
            <View style={styles.episodesSection}>
              <Text style={[styles.episodesTitle, responsiveEpisodesTitleStyle]}>
                {episodes.length} Episódio{episodes.length !== 1 ? 's' : ''}
              </Text>
              <FlatList
                data={episodes}
                scrollEnabled={false}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[
                      styles.episodeItem,
                      item.episode_number === episodeNumber && styles.episodeItemActive,
                    ]}
                    onPress={() => navigate(`/watch/${seriesId}/${item.episode_number}`)}
                  >
                    {item.thumbnail_url && (
                      <Image
                        source={{ uri: item.thumbnail_url }}
                        style={styles.episodeThumbnail}
                      />
                    )}
                    <View style={styles.episodeInfo}>
                      <Text style={styles.episodeNumber}>
                        Ep. {item.episode_number}
                      </Text>
                      <Text style={styles.episodeTitle} numberOfLines={1}>
                        {item.title}
                      </Text>
                      <Text style={styles.episodeDuration}>
                        {Math.floor(item.duration_seconds / 60)} min
                      </Text>
                    </View>
                  </TouchableOpacity>
                )}
                keyExtractor={(item) => item.id}
              />
            </View>
          )}

          <View style={styles.bottomPadding} />
        </div>
      </View>
    );
  }

  const iframeUrl = episode.m3u8_url_bunny || `https://player.mediadelivery.net/embed/${process.env.REACT_APP_BUNNY_LIBRARY_ID || '267119'}/${episode.bunny_video_id}`;

  return (
    <View style={styles.container}>
      {/* Video Player */}
      <div style={playerContainerStyle}>
        {!iframeLoaded && (
          <View style={styles.playerLoadingOverlay}>
            <View style={styles.playerLoadingContent}>
              <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
              <Text style={styles.loadingText}>Iniciando reprodução...</Text>
            </View>
          </View>
        )}
        {(episode.m3u8_url_bunny || episode.m3u8_url_source || episode.mp4_urls?.length) ? (
          <div
            style={playerVideoWrapperStyle}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => isPlaying && setShowControls(false)}
          >
            <video
              ref={videoRef}
              key={`${seriesId}-${episodeNumber}`}
              style={playerVideoStyle}
              autoPlay
              crossOrigin="anonymous"
              playsInline
              controls={!episode.m3u8_url_bunny || episode.m3u8_url_bunny.trim() === ''}
            />

            {/* Custom Controls */}
            {iframeLoaded && (
              <div
                style={{
                  ...playerControlsContainerStyle,
                  opacity: showControls ? 1 : 0,
                  pointerEvents: showControls ? 'auto' : 'none',
                } as React.CSSProperties}
              >
                {/* Progress Bar */}
                <div style={progressBarContainerStyle}>
                  <div
                    style={progressBarStyle}
                    onMouseEnter={(e) => {
                      const bar = e.currentTarget as HTMLDivElement;
                      bar.style.height = '5px';
                      bar.style.marginTop = '-1px';
                    }}
                    onMouseLeave={(e) => {
                      const bar = e.currentTarget as HTMLDivElement;
                      bar.style.height = '3px';
                      bar.style.marginTop = '0px';
                    }}
                    onClick={(e) => {
                      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      const newTime = percent * duration;
                      if (playerRef.current) {
                        playerRef.current.setCurrentTime(newTime);
                      }
                    }}
                  >
                    <div
                      style={{
                        ...progressFillStyle,
                        width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                        boxShadow: `0 0 4px ${NETFLIX_COLORS.primary}`,
                      } as React.CSSProperties}
                    />
                  </div>
                </div>

                {/* Controls Bar */}
                <div style={controlsBarStyle}>
                  <div style={controlsLeftStyle}>
                    {/* Play/Pause */}
                    <button
                      onClick={() => {
                        if (playerRef.current) {
                          isPlaying ? playerRef.current.pause() : playerRef.current.play();
                        }
                      }}
                      style={controlButtonStyle}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
                      }}
                      title={isPlaying ? 'Pausar' : 'Reproduzir'}
                    >
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </button>

                    {/* Volume */}
                    <div style={volumeContainerStyle}>
                      <button
                        onClick={() => {
                          if (playerRef.current) {
                            playerRef.current.isMuted() ? playerRef.current.unmute() : playerRef.current.mute();
                          }
                        }}
                        style={controlButtonStyle}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
                        }}
                        title={videoRef.current?.muted ? 'Desmutar' : 'Mutar'}
                      >
                        {videoRef.current?.muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={volume * 100}
                        onChange={(e) => {
                          const newVolume = parseFloat(e.currentTarget.value) / 100;
                          setVolumeState(newVolume);
                          if (playerRef.current) {
                            playerRef.current.setVolume(newVolume);
                            if (videoRef.current && newVolume > 0 && videoRef.current.muted) {
                              playerRef.current.unmute();
                            }
                          }
                        }}
                        style={volumeSliderStyle}
                      />
                    </div>

                    {/* Time Display */}
                    <span style={timeDisplayStyle}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>

                  {/* Right Controls */}
                  <div style={controlsRightStyle}>
                    {/* Speed */}
                    <select
                      value={playbackRate}
                      onChange={(e) => {
                        const rate = parseFloat(e.currentTarget.value);
                        setPlaybackRateState(rate);
                        if (playerRef.current) {
                          playerRef.current.setPlaybackRate(rate);
                        }
                      }}
                      style={selectControlStyle}
                    >
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                        <option key={rate} value={rate} style={selectOptionStyle}>
                          {rate}x
                        </option>
                      ))}
                    </select>

                    {/* Quality */}
                    {qualities.length > 0 && (
                      <select
                        value={currentQuality}
                        onChange={(e) => {
                          const quality = e.currentTarget.value;
                          setCurrentQuality(quality);
                          if (playerRef.current) {
                            if (quality === 'auto') {
                              playerRef.current.setQuality(-1);
                            } else {
                              playerRef.current.setQuality(parseInt(quality, 10));
                            }
                          }
                        }}
                        style={selectControlStyle}
                      >
                        <option value="auto" style={selectOptionStyle}>
                          Auto
                        </option>
                        {qualities.map((q) => (
                          <option key={q.index} value={q.index} style={selectOptionStyle}>
                            {q.name}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* PiP */}
                    <button
                      onClick={() => playerRef.current?.requestPictureInPicture()}
                      style={controlButtonStyle}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
                      }}
                      title="Picture in Picture"
                    >
                      <Copy size={20} />
                    </button>

                    {/* Cast */}
                    <button
                      onClick={() => playerRef.current?.requestCast()}
                      style={controlButtonStyle}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
                      }}
                      title="Transmitir para TV"
                    >
                      <Tv size={20} />
                    </button>

                    {/* Report Problem */}
                    <button
                      onClick={() => setIsReportModalOpen(true)}
                      style={controlButtonStyle}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,100,100,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
                      }}
                      title="Reportar Problema"
                    >
                      <AlertTriangle size={20} color="#ff6464" />
                    </button>

                    {/* Fullscreen */}
                    <button
                      onClick={() => playerRef.current?.toggleFullscreen()}
                      style={controlButtonStyle}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
                      }}
                      title={isFullscreen ? 'Sair de Tela Cheia' : 'Tela Cheia'}
                    >
                      {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <iframe
            key={`${seriesId}-${episodeNumber}`}
            src={iframeUrl}
            style={{
              ...playerIframeStyle,
              border: 'none',
              opacity: iframeLoaded ? 1 : 0,
            } as React.CSSProperties}
            allowFullScreen
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            onLoad={() => setIframeLoaded(true)}
          />
        )}
      </div>

      {/* Info */}
      <div style={mainWrapperStyle}>
        <View style={styles.info}>
          <Text style={styles.infoTitle}>{episode.title}</Text>
          {episode.description && (
            <Text style={styles.infoDescription}>{episode.description}</Text>
          )}
        </View>

        {/* Action Buttons */}
        <View style={[styles.actionButtons, responsiveActionButtonsStyle]}>
          <TouchableOpacity
            style={[styles.button, styles.backButton]}
            onPress={() => navigate(-1)}
          >
            <ArrowLeft size={20} color={NETFLIX_COLORS.white} />
            <Text style={[styles.buttonText, styles.playButtonText]}>Voltar</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.button,
              inWatchlist ? styles.favoriteButtonRemove : styles.favoriteButton,
            ]}
            onPress={handleToggleWatchlist}
            disabled={addToWatchlist.isPending || removeFromWatchlist.isPending}
          >
            <Heart
              size={20}
              color={inWatchlist ? NETFLIX_COLORS.white : NETFLIX_COLORS.white}
              fill={inWatchlist ? NETFLIX_COLORS.danger : 'none'}
            />
            <Text style={[styles.buttonText, inWatchlist ? styles.playButtonRemoveText : styles.playButtonText]}>
              {inWatchlist ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.button, styles.markWatchedButton]}
            onPress={handleMarkAsWatched}
            disabled={saveWatchProgress.isPending}
          >
            <Check size={20} color={NETFLIX_COLORS.white} />
            <Text style={[styles.buttonText, styles.playButtonText]}>Marcar como Assistido</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.button, styles.reportButton]}
            onPress={() => setIsReportModalOpen(true)}
          >
            <AlertTriangle size={20} color={NETFLIX_COLORS.white} />
            <Text style={[styles.buttonText, styles.reportButtonText]}>Reportar Problema</Text>
          </TouchableOpacity>
        </View>

        {/* Episodes Grid */}
        {episodes && episodes.length > 0 ? (
          <View style={styles.episodesSection}>
            <View style={styles.episodesHeader}>
              <Text style={[styles.episodesTitle, responsiveEpisodesTitleStyle]}>
                {filteredEpisodes.length} de {episodes.length} Episódio{episodes.length !== 1 ? 's' : ''}
              </Text>
              <input
                type="text"
                style={episodeSearchInputStyle}
                placeholder="Buscar episódio..."
                value={episodeSearchQuery}
                onChange={(e) => setEpisodeSearchQuery(e.target.value)}
              />
            </View>
            <div style={episodesGridContainerStyle}>
              {filteredEpisodes.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.episodeGridItem,
                    item.episode_number === episodeNumber && styles.episodeGridItemActive,
                  ]}
                  onPress={() => navigate(`/watch/${seriesId}/${item.episode_number}`)}
                >
                  <div style={episodeGridThumbnailWrapperStyle}>
                    {item.thumbnail_url ? (
                      <Image
                        source={{ uri: item.thumbnail_url }}
                        style={styles.episodeGridThumbnail}
                      />
                    ) : (
                      <div style={episodeGridPlaceholderStyle}>
                        <span style={episodeGridPlaceholderTextStyle}>
                          EP {item.episode_number}
                        </span>
                      </div>
                    )}
                  </div>
                  <View style={styles.episodeGridInfo}>
                    <Text style={styles.episodeGridNumber}>
                      Ep. {item.episode_number}
                    </Text>
                    <Text style={styles.episodeGridTitle} numberOfLines={2}>
                      {item.title}
                    </Text>
                    <Text style={styles.episodeGridDuration}>
                      {Math.floor(item.duration_seconds / 60)}m
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </div>
          </View>
        ) : (
          // Loading state ou sem episódios
          <View style={styles.episodesSection}>
            <Text style={[styles.episodesTitle, responsiveEpisodesTitleStyle]}>
              Episódios
            </Text>
            <div style={loadingEpisodesStyle}>
              <p>Carregando episódios...</p>
            </div>
          </View>
        )}

        <View style={styles.bottomPadding} />
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        seriesId={seriesId}
        episodeNumber={episodeNumber}
      />
    </View>
  );
}

const playerContainerStyle = {
  width: '100%',
  aspectRatio: '16 / 9',
  position: 'relative',
  backgroundColor: NETFLIX_COLORS.background,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as React.CSSProperties;

const playerContainerPlaceholderStyle = {
  width: '100%',
  paddingTop: '56.25%',
  position: 'relative',
  backgroundColor: NETFLIX_COLORS.background,
  overflow: 'hidden',
} as React.CSSProperties;

const playerIframeStyle = {
  width: '100%',
  height: '100%',
  border: 'none',
  borderRadius: 0,
  objectFit: 'contain',
  backgroundColor: '#000',
} as React.CSSProperties;

const mainWrapperStyle = {
  maxWidth: '1440px',
  margin: '0 auto',
  width: '100%',
  padding: `0 ${NETFLIX_SPACING.xl}px`,
} as React.CSSProperties;

const episodesGridContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: `${NETFLIX_SPACING.md}px`,
  maxHeight: '400px',
  overflowY: 'auto',
  paddingRight: '8px',
} as React.CSSProperties;

const episodeGridThumbnailWrapperStyle = {
  width: '100%',
  height: '70px',
  overflow: 'hidden',
  backgroundColor: NETFLIX_COLORS.background,
} as React.CSSProperties;

const episodeGridPlaceholderStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 107, 0, 0.15)',
} as React.CSSProperties;

const loadingEpisodesStyle = {
  padding: 20,
  textAlign: 'center',
  color: NETFLIX_COLORS.textSecondary,
} as React.CSSProperties;

const episodeSearchInputStyle = {
  width: '100%',
  backgroundColor: NETFLIX_COLORS.surface,
  borderRadius: '8px',
  paddingLeft: `${NETFLIX_SPACING.md}px`,
  paddingRight: `${NETFLIX_SPACING.md}px`,
  paddingTop: `${NETFLIX_SPACING.sm}px`,
  paddingBottom: `${NETFLIX_SPACING.sm}px`,
  color: NETFLIX_COLORS.white,
  fontSize: '14px',
  border: `1px solid ${NETFLIX_COLORS.border}`,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
} as React.CSSProperties;

const episodeGridPlaceholderTextStyle = {
  color: NETFLIX_COLORS.primary,
  fontSize: '12px',
  fontWeight: 'bold',
  textAlign: 'center',
} as React.CSSProperties;

// Player Controls Styles
const playerVideoWrapperStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#000',
} as React.CSSProperties;

const playerVideoStyle = {
  width: '100%',
  height: '100%',
  border: 'none',
  display: 'block',
} as React.CSSProperties;

const playerControlsContainerStyle = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.5), transparent)',
  opacity: 1,
  transition: 'opacity 0.2s ease-out',
  padding: '40px 20px 16px 20px',
  zIndex: 50,
  pointerEvents: 'auto',
} as React.CSSProperties;

const progressBarContainerStyle = {
  marginBottom: '12px',
  cursor: 'pointer',
} as React.CSSProperties;

const progressBarStyle = {
  width: '100%',
  height: '3px',
  backgroundColor: 'rgba(255,255,255,0.3)',
  borderRadius: '1.5px',
  position: 'relative',
  overflow: 'hidden',
} as React.CSSProperties;

const progressFillStyle = {
  height: '100%',
  backgroundColor: NETFLIX_COLORS.primary,
  borderRadius: '1.5px',
  transition: 'width 0.05s linear',
} as React.CSSProperties;

const controlsBarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  justifyContent: 'space-between',
} as React.CSSProperties;

const controlsLeftStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
} as React.CSSProperties;

const controlsRightStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
} as React.CSSProperties;

const controlButtonStyle = {
  background: 'rgba(255,255,255,0.1)',
  border: 'none',
  color: NETFLIX_COLORS.white,
  cursor: 'pointer',
  padding: '8px 10px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.2s',
} as React.CSSProperties;

const volumeContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
} as React.CSSProperties;

const volumeSliderStyle = {
  width: '80px',
  cursor: 'pointer',
  accentColor: NETFLIX_COLORS.primary,
} as React.CSSProperties;

const timeDisplayStyle = {
  color: NETFLIX_COLORS.white,
  fontSize: '14px',
  fontWeight: '500',
  minWidth: '100px',
} as React.CSSProperties;

const selectControlStyle = {
  background: 'rgba(30,30,30,0.9)',
  color: NETFLIX_COLORS.white,
  border: `1px solid ${NETFLIX_COLORS.primary}`,
  borderRadius: '4px',
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
} as React.CSSProperties;

const selectOptionStyle = {
  background: '#1e1e1e',
  color: NETFLIX_COLORS.white,
} as React.CSSProperties;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: NETFLIX_COLORS.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: NETFLIX_COLORS.background,
  },
  playerLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  playerLoadingContent: {
    alignItems: 'center',
  },
  loadingText: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 14,
    marginTop: NETFLIX_SPACING.lg,
  },
  placeholderContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    gap: NETFLIX_SPACING.lg,
    backgroundColor: NETFLIX_COLORS.background,
  },
  placeholderTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 24,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.md,
  },
  placeholderText: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 14,
  },
  info: {
    paddingVertical: NETFLIX_SPACING.xxxl,
  },
  infoTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 28,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.md,
  },
  infoDescription: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 14,
    lineHeight: 20,
  },
  actionButtons: {
    marginBottom: NETFLIX_SPACING.xxxl,
    flexDirection: 'row',
    gap: NETFLIX_SPACING.md,
    justifyContent: 'flex-end',
  },
  button: {
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    paddingVertical: NETFLIX_SPACING.md,
    paddingHorizontal: NETFLIX_SPACING.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
    gap: NETFLIX_SPACING.xs,
  },
  backButton: {
    backgroundColor: NETFLIX_COLORS.primary,
    borderColor: NETFLIX_COLORS.primary,
  },
  favoriteButton: {
    backgroundColor: NETFLIX_COLORS.primary,
    borderColor: NETFLIX_COLORS.primary,
  },
  favoriteButtonRemove: {
    backgroundColor: NETFLIX_COLORS.danger,
    borderColor: NETFLIX_COLORS.danger,
  },
  markWatchedButton: {
    backgroundColor: NETFLIX_COLORS.primary,
    borderColor: NETFLIX_COLORS.primary,
  },
  playButton: {
    backgroundColor: NETFLIX_COLORS.primary,
    borderColor: NETFLIX_COLORS.primary,
  },
  reportButton: {
    backgroundColor: NETFLIX_COLORS.danger,
    borderColor: NETFLIX_COLORS.danger,
  },
  reportButtonText: {
    color: NETFLIX_COLORS.white,
  },
  buttonText: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 12,
    fontWeight: '600',
  },
  favoriteButtonText: {
    color: NETFLIX_COLORS.white,
  },
  favoriteButtonRemoveText: {
    color: NETFLIX_COLORS.white,
  },
  playButtonText: {
    color: NETFLIX_COLORS.white,
  },
  playButtonRemoveText: {
    color: NETFLIX_COLORS.white,
  },
  episodesSection: {
    marginBottom: NETFLIX_SPACING.xl,
  },
  episodesTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.md,
  },
  episodesHeader: {
    marginBottom: NETFLIX_SPACING.lg,
  },
  episodeGridItem: {
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  episodeGridItemActive: {
    borderColor: NETFLIX_COLORS.primary,
    borderWidth: 2,
  },
  episodeGridThumbnail: {
    width: '100%',
    height: 70,
    backgroundColor: NETFLIX_COLORS.background,
  },
  episodeGridInfo: {
    padding: NETFLIX_SPACING.sm,
  },
  episodeGridNumber: {
    color: NETFLIX_COLORS.primary,
    fontSize: 10,
    fontWeight: '700',
    marginBottom: 2,
  },
  episodeGridTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 2,
    lineHeight: 14,
  },
  episodeGridDuration: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 10,
  },
  // Legacy (mantém compatibilidade)
  episodeItem: {
    flexDirection: 'row',
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: NETFLIX_SPACING.md,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  episodeItemActive: {
    borderColor: NETFLIX_COLORS.primary,
    borderWidth: 2,
  },
  episodeThumbnail: {
    width: 160,
    height: 90,
    backgroundColor: NETFLIX_COLORS.background,
  },
  episodeInfo: {
    flex: 1,
    padding: NETFLIX_SPACING.lg,
    justifyContent: 'center',
  },
  episodeNumber: {
    color: NETFLIX_COLORS.primary,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.xs,
  },
  episodeTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: NETFLIX_SPACING.xs,
  },
  episodeDuration: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 12,
  },
  bottomPadding: {
    height: 100,
  },
});
